// Copyright (c) 2025. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react';
import './BrownPaperContentSection.scss';
import { useTranslation } from 'react-i18next';
import { firestore } from '../../context/firebase';
import { Building2, BarChart3, PaintBucket } from 'lucide-react';
import PercentRangeBar from './PercentRangeBar';
import { useAuthStore } from '../../stores/authStore';
import {
  getLabelDisplayNameMatchingGroup,
  getLabelIdMatchingGroup,
} from '../../shared_components/utils/FirebaseLabelsetsUtils';
import {
  useIndustryBrownPaperBenchmarks,
  useSupplierBrownPaperBenchmarks,
} from '../../shared_components/hooks/useBrownPaperBenchmarks';

export default function BrownPaperContentSection({ delivery, setAnalysisSummary }) {
  const { i18n, t } = useTranslation();
  const [expanded, setExpanded] = React.useState(false);
  const suppliers = useAuthStore((state) => state.suppliers);
  const globalLabelsets = useAuthStore((state) => state.globalLabelsets);
  const currentUserClientId = useAuthStore((state) => state.currentUser?.company?.company);
  const [benchmarkData, setBenchmarkData] = React.useState({
    industry: { min: null, avrg: null, max: null, std: null },
    supplier: { min: null, avrg: null, max: null, std: null },
    loading: true,
  });

  const { industryAverages, loading: industryLoading } = useIndustryBrownPaperBenchmarks(
    firestore,
    currentUserClientId,
    globalLabelsets,
  );
  const { supplierAverages, loading: supplierLoading } = useSupplierBrownPaperBenchmarks(
    firestore,
    currentUserClientId,
    delivery.supplier_id,
    globalLabelsets,
  );

  // Derive industry and supplier benchmarks from the processed averages
  React.useEffect(() => {
    const materialKey = getLabelIdMatchingGroup(delivery?.delivery_labels, globalLabelsets, 'materials');
    const industryData = industryAverages[materialKey];
    const supplierData = supplierAverages[materialKey];

    setBenchmarkData({
      industry: {
        min: industryData?.min ?? null,
        avrg: industryData?.average ?? null,
        max: industryData?.max ?? null,
        std: industryData?.std ?? null,
      },
      supplier: {
        min: supplierData?.min ?? null,
        avrg: supplierData?.average ?? null,
        max: supplierData?.max ?? null,
        std: supplierData?.std ?? null,
      },
      loading: industryLoading || supplierLoading,
    });
  }, [industryAverages, supplierAverages, delivery, industryLoading, supplierLoading]);

  const brownPaperAvrg = delivery?.analytics?.data_per_labelset?.brown_paper_content?.average;
  // For min/max we use 2 standard deviations to show the range of the data (includes 95% of the data)
  const supplierMin = Math.round(Math.max(0, benchmarkData.supplier.avrg - 2 * benchmarkData.supplier.std) * 100);
  const supplierMax = Math.round(Math.min(1, benchmarkData.supplier.avrg + 2 * benchmarkData.supplier.std) * 100);
  const industryMin = Math.round(Math.max(0, benchmarkData.industry.avrg - 2 * benchmarkData.industry.std) * 100);
  const industryMax = Math.round(Math.min(1, benchmarkData.industry.avrg + 2 * benchmarkData.industry.std) * 100);

  React.useEffect(() => {
    setAnalysisSummary((prev) => ({
      ...prev,
      avgBrownPaperContent: brownPaperAvrg,
    }));
  }, [brownPaperAvrg]);

  return (
    <div className='delivery--ai-nested-card expandable'>
      <div className='delivery--ai-nested-card-header' onClick={() => setExpanded(!expanded)}>
        <div className='delivery--ai-nested-card-info'>
          <div className='delivery--ai-nested-card-icon-wrapper brown'>
            <PaintBucket className='delivery--ai-nested-card-icon' />
          </div>
          <div className='delivery--ai-nested-card-text'>
            <h4 className='delivery--ai-nested-card-title'>
              {t('brown_paper_content')}
              <span className='beta-badge'>BETA</span>
            </h4>
            <p className='delivery--ai-nested-card-subtitle'>
              {t('material')}:{' '}
              {getLabelDisplayNameMatchingGroup(
                delivery.delivery_labels,
                globalLabelsets,
                'materials',
                i18n.language,
              ) || 'Kaufhaus'}
            </p>
          </div>
        </div>
        <div className='delivery--ai-nested-card-value'>
          <div className='delivery--ai-nested-card-main-value'>
            {brownPaperAvrg ? `${Math.round(brownPaperAvrg * 100)}%` : t('no_data')}
          </div>
          {brownPaperAvrg &&
            benchmarkData.supplier.avrg !== null &&
            (() => {
              const supplierMean = Math.round(benchmarkData.supplier.avrg * 100);
              const computedPercentage = Math.round(brownPaperAvrg * 100);
              const difference = computedPercentage - supplierMean;
              const isAboveMean = difference >= 0;
              return (
                <div className={`delivery--ai-nested-card-trend ${isAboveMean ? 'positive' : 'negative'}`}>
                  <span className='delivery--ai-trend-arrow'>{isAboveMean ? '\u2197\uFE0E' : '\u2198\uFE0E'}</span>
                  <span className='delivery--ai-trend-value'>{Math.abs(difference)}%</span>
                </div>
              );
            })()}
        </div>
      </div>

      {expanded && (
        <div className='delivery--ai-nested-card-expanded'>
          <div className='delivery--ai-expanded-content'>
            {benchmarkData.loading && (
              <div className='delivery--brown-content-no-data-message'>
                <div className='delivery--brown-content-no-data-icon'>⏳</div>
                <p>Loading benchmark data...</p>
              </div>
            )}
            {!benchmarkData.loading && !brownPaperAvrg && (
              <div className='delivery--brown-content-no-data-message'>
                <div className='delivery--brown-content-no-data-icon'>ℹ</div>
                <p>{t('no_bales_analyzed_brown_content_message')}</p>
              </div>
            )}
            {!benchmarkData.loading && benchmarkData.supplier.avrg === null && benchmarkData.industry.avrg === null && (
              <div className='delivery--brown-content-no-data-message'>
                <div className='delivery--brown-content-no-data-icon'>ℹ</div>
                <p>{t('no_historical_data_supplier_benchmarks_message')}</p>
              </div>
            )}

            {!benchmarkData.loading &&
              (benchmarkData.supplier.avrg !== null || benchmarkData.industry.avrg !== null) && (
                <div className='delivery--brown-content-charts'>
                  {benchmarkData.supplier.avrg !== null && (
                    <div className='delivery--brown-content-chart'>
                      <div className='delivery--brown-content-chart-header'>
                        <div className='delivery--brown-content-chart-title'>
                          <div className='delivery--brown-content-chart-icon'>
                            <Building2 size={16} />
                          </div>
                          <h5>{suppliers?.[delivery.supplier_id]?.display_name || 'Supplier'}</h5>
                        </div>
                        {brownPaperAvrg &&
                          benchmarkData.supplier.avrg !== null &&
                          (() => {
                            const supplierMean = Math.round(benchmarkData.supplier.avrg * 100);
                            const computedPercentage = Math.round(brownPaperAvrg * 100);
                            const difference = computedPercentage - supplierMean;
                            const isAboveMean = difference >= 0;
                            return (
                              <div className={`delivery--brown-content-trend ${isAboveMean ? 'positive' : 'negative'}`}>
                                <span className='delivery--ai-trend-arrow'>
                                  {isAboveMean ? '\u2197\uFE0E' : '\u2198\uFE0E'}
                                </span>
                                <span>{Math.abs(difference)}%</span>
                              </div>
                            );
                          })()}
                      </div>
                      <div className='delivery--brown-content-timeframe'>(last 3 months)</div>
                      <PercentRangeBar
                        valuePct={brownPaperAvrg ? Math.round(brownPaperAvrg * 100) : null}
                        meanPct={
                          benchmarkData.supplier.avrg !== null ? Math.round(benchmarkData.supplier.avrg * 100) : null
                        }
                        minPct={supplierMin}
                        maxPct={supplierMax}
                      />
                    </div>
                  )}

                  {benchmarkData.industry.avrg !== null && (
                    <div className='delivery--brown-content-chart'>
                      <div className='delivery--brown-content-chart-header'>
                        <div className='delivery--brown-content-chart-title'>
                          <div className='delivery--brown-content-chart-icon'>
                            <BarChart3 size={16} />
                          </div>
                          <h5>
                            {t('industry_benchmark')}{' '}
                            {getLabelDisplayNameMatchingGroup(
                              delivery.delivery_labels,
                              globalLabelsets,
                              'materials',
                              i18n.language,
                            ) || 'Kaufhaus'}
                          </h5>
                        </div>
                        {brownPaperAvrg &&
                          benchmarkData.industry.avrg !== null &&
                          (() => {
                            const industryMean = Math.round(benchmarkData.industry.avrg * 100);
                            const computedPercentage = Math.round(brownPaperAvrg * 100);
                            const difference = computedPercentage - industryMean;
                            const isAboveMean = difference >= 0;
                            return (
                              <div className={`delivery--brown-content-trend ${isAboveMean ? 'positive' : 'negative'}`}>
                                <span className='delivery--ai-trend-arrow'>
                                  {isAboveMean ? '\u2197\uFE0E' : '\u2198\uFE0E'}
                                </span>
                                <span>{Math.abs(difference)}%</span>
                              </div>
                            );
                          })()}
                      </div>
                      <div className='delivery--brown-content-timeframe'>(last 3 months)</div>
                      <PercentRangeBar
                        valuePct={brownPaperAvrg ? Math.round(brownPaperAvrg * 100) : null}
                        meanPct={
                          benchmarkData.industry.avrg !== null ? Math.round(benchmarkData.industry.avrg * 100) : null
                        }
                        minPct={industryMin}
                        maxPct={industryMax}
                      />
                    </div>
                  )}
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
}
